import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowLeft, MessageCircle } from "lucide-react";
import { CATEGORIES, BRAND } from "@/data/site";
import { CAT_DESC_AR } from "@/data/content_ar";
import { LogoMark } from "@/components/LogoMark";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useI18n } from "@/context/I18nContext";

const SPECS = [
  { label: "Packing", value: "As per buyer requirement — bulk bags, cartons, retail packs or private label" },
  { label: "Minimum Order", value: "Flexible — trial samples to full 20ft / 40ft containers" },
  { label: "Grading", value: "Sortex-cleaned, moisture-checked, lab-verified export grade" },
  { label: "Lead Time", value: "Typically 2–4 weeks from confirmed order, subject to volume" },
  { label: "Documentation", value: "Phytosanitary, COO, APEDA/Spice Board certs, full export papers" },
  { label: "Shipping", value: "FOB / CIF to your destination port via trusted freight partners" },
];

export default function ProductDetail() {
  const { id } = useParams();
  const { t, lang } = useI18n();
  const cat = CATEGORIES.find((c) => c.id === id) || CATEGORIES[0];
  const desc = lang === "ar" ? CAT_DESC_AR[cat.id] || cat.desc : cat.desc;

  return (
    <div className="bg-brand-bg text-brand-fg font-sans min-h-screen noise-overlay">
      <Header />
      <main className="mx-auto max-w-7xl px-6 lg:px-10 pt-32 pb-24">
        <Link to="/#products" className="inline-flex items-center gap-2 text-sm text-brand-fg/60 hover:text-brand-gold transition-colors">
          <ArrowLeft size={15} /> {t("nav_products")}
        </Link>

        <div className="mt-6 grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="relative overflow-hidden rounded-2xl border border-brand-border shadow-sm aspect-square">
            <img src={cat.image} alt={cat.name} className="absolute inset-0 h-full w-full object-cover" />
            <LogoMark className="absolute top-5 left-5 z-10" />
          </motion.div>

          <div>
            <div className="font-display text-5xl text-brand-gold/30">{cat.caption}</div>
            <h1 className="mt-2 font-display text-4xl sm:text-5xl tracking-tight text-brand-fg">{cat.name}</h1>
            <p className="mt-4 text-brand-fg/70 leading-relaxed">{desc}</p>

            <div className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-1">
              {cat.items.map((item) => (
                <div key={item} className="flex items-center gap-3 border-b border-brand-border py-3">
                  <span className="grid place-items-center h-6 w-6 rounded-full bg-brand-goldSoft text-brand-gold shrink-0"><Check size={13} /></span>
                  <span className="text-brand-fg/85 text-sm">{item}</span>
                </div>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/#inquiry" className="rounded-full bg-brand-gold px-7 py-3 text-sm font-semibold text-white hover:bg-brand-gold/90 transition-colors">{t("cta_quote")}</Link>
              <a href={`https://wa.me/${BRAND.whatsappRaw}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-full border border-brand-fg/20 px-6 py-3 text-sm font-semibold text-brand-fg hover:border-brand-gold hover:text-brand-gold transition-colors"><MessageCircle size={16} /> {t("hero_whatsapp")}</a>
            </div>
          </div>
        </div>

        <div className="mt-16">
          <h2 className="font-display text-2xl text-brand-fg">Export Specifications</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {SPECS.map((s) => (
              <div key={s.label} className="rounded-2xl border border-brand-border bg-brand-card p-6 shadow-sm">
                <div className="text-[11px] uppercase tracking-[0.2em] text-brand-gold">{s.label}</div>
                <div className="mt-2 text-sm text-brand-fg/80 leading-relaxed">{s.value}</div>
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs text-brand-muted">Specifications are indicative. Exact grade, packing and MOQ are confirmed per enquiry.</p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
